import { useEffect, useState } from "react";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";
import { Spinner } from "@/components/ui/Spinner";
import {
  fetchInvoiceMailAttachment,
  invoiceMailAttachmentUrl,
  type InvoiceMailAttachmentSummary,
} from "@/lib/invoiceMail";
import { useMaMediaContextMenu } from "@/pages/material/useMaMediaContextMenu";

export function isPdfAttachment(att: InvoiceMailAttachmentSummary): boolean {
  const ct = (att.contentType || "").toLowerCase();
  const name = (att.name || "").toLowerCase();
  return ct === "application/pdf" || name.endsWith(".pdf");
}

export function isImageAttachment(att: InvoiceMailAttachmentSummary): boolean {
  const ct = (att.contentType || "").toLowerCase();
  return ct.startsWith("image/");
}

export function isXmlAttachment(att: InvoiceMailAttachmentSummary): boolean {
  const ct = (att.contentType || "").toLowerCase();
  const name = (att.name || "").toLowerCase();
  return ct.includes("xml") || name.endsWith(".xml");
}

export function InvoiceMailAttachmentPreview({
  candidateId,
  attachment,
  className,
}: {
  candidateId: number;
  attachment: InvoiceMailAttachmentSummary;
  className?: string;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [xmlText, setXmlText] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { openMediaContextMenu, contextMenuNode } = useMaMediaContextMenu();

  const attId = attachment.id || "";

  useEffect(() => {
    if (!attId) {
      setLoading(false);
      setError("Anhang-ID fehlt.");
      return;
    }
    let revoked: string | null = null;
    setLoading(true);
    setError(null);
    setUrl(null);
    setPdfBlob(null);
    setXmlText(null);
    void fetchInvoiceMailAttachment(candidateId, attId)
      .then(async (blob) => {
        if (isXmlAttachment(attachment)) {
          const text = await blob.text();
          setXmlText(text.length > 120_000 ? `${text.slice(0, 120_000)}\n\n… (gekürzt)` : text);
          return;
        }
        if (isPdfAttachment(attachment)) {
          setPdfBlob(blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" }));
          return;
        }
        revoked = URL.createObjectURL(blob);
        setUrl(revoked);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : "Vorschau konnte nicht geladen werden.");
      })
      .finally(() => setLoading(false));

    return () => {
      if (revoked) URL.revokeObjectURL(revoked);
    };
  }, [candidateId, attId, attachment]);

  const frameClass =
    className || "min-h-[min(55vh,36rem)] w-full rounded-xl border border-separator bg-white";

  if (loading) {
    return (
      <div className="flex min-h-[min(50vh,32rem)] items-center justify-center rounded-xl border border-separator bg-fill-quaternary/15">
        <Spinner />
      </div>
    );
  }
  if (error) {
    return (
      <p className="rounded-xl border border-status-red/30 bg-status-red/10 px-4 py-8 text-center text-sm text-status-red">
        {error}
      </p>
    );
  }

  if (xmlText != null) {
    return (
      <pre className="max-h-[min(55vh,36rem)] overflow-auto rounded-xl border border-separator bg-fill-quaternary/10 p-3 text-xs leading-relaxed text-label-primary">
        {xmlText}
      </pre>
    );
  }

  if (isPdfAttachment(attachment)) {
    if (!pdfBlob) return null;
    return <InlinePdfView blob={pdfBlob} title={attachment.name || "Rechnung"} className={frameClass} />;
  }

  if (!url) return null;
  if (isImageAttachment(attachment)) {
    return (
      <>
        <div className="flex min-h-[min(40vh,28rem)] items-center justify-center rounded-xl border border-separator bg-fill-quaternary/15 p-4">
          <img
            src={url}
            alt={attachment.name || "Anhang"}
            className="max-h-[min(60vh,40rem)] max-w-full object-contain"
            onContextMenu={(e) => {
              const imageElement =
                e.target instanceof HTMLImageElement ? e.target : null;
              openMediaContextMenu(e, {
                url,
                filename: attachment.name || "bild.jpg",
                contentType: attachment.contentType || "image/jpeg",
                isImage: true,
                imageElement,
              });
            }}
          />
        </div>
        {contextMenuNode}
      </>
    );
  }

  return (
    <p className="rounded-xl border border-separator bg-fill-quaternary/15 px-4 py-6 text-center text-sm text-label-secondary">
      Vorschau für diesen Dateityp nicht verfügbar.{" "}
      <a
        href={invoiceMailAttachmentUrl(candidateId, attId)}
        className="font-medium text-accent underline"
        target="_blank"
        rel="noreferrer"
      >
        Datei herunterladen
      </a>
    </p>
  );
}
